import type { CurrencyCode, KhqrCurrency, Language, Rates } from '@expenditure/core';
import type {
  Balances,
  DashboardSummary,
  Expense,
  ExpenseInput,
  Group,
  GroupDetail,
  GroupListItem,
  GroupStats,
  InvitePreview,
  KhqrForPayment,
  KhqrOrder,
  Member,
  Payment,
  PaymentRequest,
  PaymentRequestResult,
  Plan,
  Recurring,
  ReceiptDraft,
  Session,
  User,
} from './types';

const BASE_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

/** Absolute URL for a path the API returned (e.g. a signed image link). */
export const apiUrl = (path: string): string => new URL(path, BASE_URL || window.location.origin).toString();

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let token: string | null = null;
/** Called on a 401 to obtain a fresh token (e.g. by re-verifying Telegram initData). */
let refreshSession: (() => Promise<string | null>) | null = null;

export function setAuth(newToken: string | null, refresh?: () => Promise<string | null>): void {
  token = newToken;
  if (refresh) refreshSession = refresh;
}

/** Signs out for good (after deleting the account): no silent sign-in again. */
export function clearAuth(): void {
  token = null;
  refreshSession = null;
}

async function request<T>(method: string, path: string, body?: unknown, retry = true): Promise<T> {
  // Blobs (photos) are sent as-is with their own type; everything else as JSON.
  const isBlob = body instanceof Blob;
  const response = await fetch(`${BASE_URL}/api${path}`, {
    method,
    headers: {
      ...(body !== undefined ? { 'Content-Type': isBlob ? body.type : 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : isBlob ? body : JSON.stringify(body),
  });

  if (response.status === 401 && retry && refreshSession && !path.startsWith('/auth')) {
    const fresh = await refreshSession();
    if (fresh) {
      token = fresh;
      return request<T>(method, path, body, false);
    }
  }
  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = payload?.error ?? {};
    throw new ApiError(response.status, error.code ?? 'UNKNOWN', error.message ?? response.statusText, error.details);
  }
  return payload as T;
}

/** Fetches a file (e.g. CSV) with the session token, for a browser download. */
async function download(path: string, retry = true): Promise<Blob> {
  const response = await fetch(`${BASE_URL}/api${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  // Same silent session refresh as JSON requests.
  if (response.status === 401 && retry && refreshSession) {
    const fresh = await refreshSession();
    if (fresh) {
      token = fresh;
      return download(path, false);
    }
  }
  if (!response.ok) {
    const error = (await response.json().catch(() => null))?.error ?? {};
    throw new ApiError(response.status, error.code ?? 'UNKNOWN', error.message ?? response.statusText);
  }
  return response.blob();
}

const get = <T>(path: string) => request<T>('GET', path);
const post = <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {});
const put = <T>(path: string, body: unknown) => request<T>('PUT', path, body);
const patch = <T>(path: string, body: unknown) => request<T>('PATCH', path, body);
const del = (path: string) => request<void>('DELETE', path);

export const api = {
  loginWithTelegram: (initData: string) => post<Session>('/auth/telegram', { initData }),
  loginDev: (telegramId: number, firstName: string) => post<Session>('/auth/dev', { telegramId, firstName }),

  me: () => get<{ user: User }>('/me'),
  updateMe: (prefs: {
    language?: Language;
    defaultCurrency?: KhqrCurrency;
    botAllowed?: boolean;
    notifyExpenses?: boolean;
    notifyReminders?: boolean;
  }) => patch<{ user: User }>('/me', prefs),
  deleteAccount: () => del('/me'),
  setKhqr: (payload: string) => put<{ user: User }>('/me/khqr', { payload }),
  removeKhqr: () => request<{ user: User }>('DELETE', '/me/khqr'),
  dashboard: () => get<DashboardSummary>('/me/summary'),

  groups: () => get<{ groups: GroupListItem[] }>('/groups'),
  createGroup: (input: { name: string; description?: string | null; currency: CurrencyCode; khrPerUsd?: number; rates?: Rates }) =>
    post<GroupDetail>('/groups', input),
  /** The rates a new group starts with (units per US dollar). */
  rates: () => get<{ rates: Group['rates'] }>('/rates'),
  group: (groupId: string) => get<GroupDetail>(`/groups/${groupId}`),
  updateGroup: (groupId: string, input: { name?: string; description?: string | null; khrPerUsd?: number; rates?: Rates }) =>
    patch<{ group: Group }>(`/groups/${groupId}`, input),
  deleteGroup: (groupId: string) => del(`/groups/${groupId}`),
  regenerateInvite: (groupId: string) => post<{ inviteCode: string }>(`/groups/${groupId}/invite/regenerate`),
  previewInvite: (code: string) => get<InvitePreview>(`/groups/invite/${encodeURIComponent(code)}`),
  joinGroup: (inviteCode: string, claimMemberId?: string) =>
    post<GroupDetail>('/groups/join', { inviteCode, claimMemberId }),

  addGuest: (groupId: string, displayName: string) =>
    post<{ member: Member }>(`/groups/${groupId}/members`, { displayName }),
  updateMember: (groupId: string, memberId: string, input: { displayName?: string; role?: 'admin' | 'member' }) =>
    patch<{ member: Member }>(`/groups/${groupId}/members/${memberId}`, input),
  removeMember: (groupId: string, memberId: string) => del(`/groups/${groupId}/members/${memberId}`),

  expenses: (groupId: string, cursor?: string) =>
    get<{ expenses: Expense[]; nextCursor: string | null }>(
      `/groups/${groupId}/expenses?limit=30${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),
  expense: (groupId: string, expenseId: string) => get<{ expense: Expense }>(`/groups/${groupId}/expenses/${expenseId}`),
  createExpense: (groupId: string, input: ExpenseInput) =>
    post<{ expense: Expense }>(`/groups/${groupId}/expenses`, input),
  updateExpense: (groupId: string, expenseId: string, input: ExpenseInput) =>
    put<{ expense: Expense }>(`/groups/${groupId}/expenses/${expenseId}`, input),
  deleteExpense: (groupId: string, expenseId: string) => del(`/groups/${groupId}/expenses/${expenseId}`),
  recurring: (groupId: string) => get<{ recurring: Recurring[] }>(`/groups/${groupId}/recurring`),
  stopRecurring: (groupId: string, recurringId: string) => del(`/groups/${groupId}/recurring/${recurringId}`),

  balances: (groupId: string) => get<Balances>(`/groups/${groupId}/balances`),
  stats: (groupId: string) => get<GroupStats>(`/groups/${groupId}/stats`),
  exportCsv: (groupId: string) => download(`/groups/${groupId}/export.csv`),
  exportToTelegram: (groupId: string) =>
    post<{ delivered: boolean; reason: string | null }>(`/groups/${groupId}/export/telegram`),
  payments: (groupId: string) => get<{ payments: Payment[] }>(`/groups/${groupId}/payments`),
  createPayment: (
    groupId: string,
    input: { fromMemberId: string; toMemberId: string; amount: string; note?: string; settle?: boolean },
  ) =>
    post<{ payment: Payment }>(`/groups/${groupId}/payments`, input),
  deletePayment: (groupId: string, paymentId: string) => del(`/groups/${groupId}/payments/${paymentId}`),
  features: () => get<{ receiptScan: boolean }>('/features'),
  scanReceipt: (groupId: string, photo: Blob) =>
    post<{ draft: ReceiptDraft; plan: Plan }>(`/groups/${groupId}/receipts/scan`, photo),
  plan: () => get<Plan>('/me/plan'),
  proCheckout: () => post<{ invoiceLink: string }>('/me/pro/checkout'),
  proKhqr: () => post<KhqrOrder>('/me/pro/khqr'),
  tipKhqr: (amount: string) => post<KhqrOrder>('/me/tip/khqr', { amount }),
  checkKhqrOrder: (orderId: string) =>
    post<{ status: KhqrOrder['status']; plan: Plan }>(`/me/khqr-orders/${orderId}/check`),
  paymentRequests: (groupId: string) => get<{ requests: PaymentRequest[] }>(`/groups/${groupId}/payment-requests`),
  requestPayment: (groupId: string, input: { fromMemberId: string; amount: string }) =>
    post<PaymentRequestResult>(`/groups/${groupId}/payment-requests`, input),
  memberKhqr: (groupId: string, memberId: string, amount: string) =>
    get<KhqrForPayment>(`/groups/${groupId}/members/${memberId}/khqr?amount=${encodeURIComponent(amount)}`),
};
